import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Check, ShieldCheck, Sparkles, Info } from "lucide-react";

const T = {
  pt: {
    overline: "Planos Comerciais",
    title: "Licença PrimeForms Pro",
    subtitle: "Para profissionais que preenchem formulários USCIS em escala. Gere PDFs ilimitados enquanto sua licença estiver ativa.",
    monthly: "Mensal",
    yearly: "Anual",
    save_note: "2 meses grátis",
    per_month: "/mês",
    per_year: "/ano",
    features_title: "O que está incluído",
    cta_pending: "Aguardando ativação",
    cta_active: "Você já tem essa licença ativa",
    cta_start: "Falar para ativar",
    cta_login: "Faça login como comercial",
    disclaimer: "Este plano dá acesso ao sistema para gerar PDFs ilimitados enquanto ativo. Não inclui consultoria jurídica.",
    payment_note: "Pagamento inicial via Zelle/Venmo. Ativação manual até 24h. Stripe checkout automático em breve.",
    features: [
      "PDFs ilimitados enquanto a licença estiver ativa",
      "Acesso a todos os 8 formulários USCIS",
      "Combo Packages (AOS familiar com dependentes)",
      "Advisor determinístico para triagem",
      "Checklist de evidências por formulário",
      "SSN criptografado (AES-256)",
      "Suporte por WhatsApp em português",
      "Trilíngue (PT / ES / EN)",
    ],
  },
  es: {
    overline: "Planes Comerciales",
    title: "Licencia PrimeForms Pro",
    subtitle: "Para profesionales que llenan formularios USCIS a escala. Genere PDFs ilimitados mientras su licencia esté activa.",
    monthly: "Mensual",
    yearly: "Anual",
    save_note: "2 meses gratis",
    per_month: "/mes",
    per_year: "/año",
    features_title: "Qué está incluido",
    cta_pending: "Esperando activación",
    cta_active: "Ya tiene esta licencia activa",
    cta_start: "Hablar para activar",
    cta_login: "Inicie sesión como comercial",
    disclaimer: "Este plan da acceso al sistema para generar PDFs ilimitados mientras esté activo. No incluye asesoría legal.",
    payment_note: "Pago inicial vía Zelle/Venmo. Activación manual hasta 24h. Checkout Stripe automático próximamente.",
    features: [
      "PDFs ilimitados mientras la licencia esté activa",
      "Acceso a los 8 formularios USCIS",
      "Combo Packages (AOS familiar con dependientes)",
      "Advisor determinístico para triaje",
      "Checklist de evidencias por formulario",
      "SSN cifrado (AES-256)",
      "Soporte por WhatsApp en español",
      "Trilingüe (PT / ES / EN)",
    ],
  },
  en: {
    overline: "Commercial Plans",
    title: "PrimeForms Pro License",
    subtitle: "For professionals filling USCIS forms at scale. Generate unlimited PDFs while your license is active.",
    monthly: "Monthly",
    yearly: "Yearly",
    save_note: "2 months free",
    per_month: "/mo",
    per_year: "/yr",
    features_title: "What's included",
    cta_pending: "Awaiting activation",
    cta_active: "You already have this license active",
    cta_start: "Contact to activate",
    cta_login: "Sign in as commercial",
    disclaimer: "This plan gives access to the system for generating unlimited PDFs while active. Does not include legal advice.",
    payment_note: "Initial payment via Zelle/Venmo. Manual activation within 24h. Stripe checkout coming soon.",
    features: [
      "Unlimited PDFs while license is active",
      "Access to all 8 USCIS forms",
      "Combo Packages (family AOS with dependents)",
      "Deterministic Advisor for triage",
      "Evidence checklist per form",
      "Encrypted SSN (AES-256)",
      "WhatsApp support in English",
      "Trilingual (PT / ES / EN)",
    ],
  },
};

const PLANS = [
  { id: "monthly", price: 79, currency: "USD", period_key: "per_month", label_key: "monthly", accent: false },
  { id: "yearly", price: 790, currency: "USD", period_key: "per_year", label_key: "yearly", accent: true, badge_key: "save_note" },
];

export default function PricingPage() {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const tr = T[lang] || T.pt;

  const license = user?.license;
  const isComercialActive = license?.license_type === "comercial" && license?.status === "active";
  const isComercialPending = license?.license_type === "comercial" && license?.status !== "active";
  const isParticular = license?.license_type === "particular";

  const contactHref = `mailto:sales@immiform.app?subject=${encodeURIComponent("Ativação de Licença Comercial")}&body=${encodeURIComponent("Olá, quero ativar minha licença comercial PrimeForms Pro.")}`;

  return (
    <div data-testid="pricing-page" className="mx-auto max-w-6xl px-6 py-16">
      {/* Header */}
      <div className="mb-14 text-center">
        <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[hsl(var(--gold))]">
          {tr.overline}
        </div>
        <h1 className="font-display mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
          {tr.title}
        </h1>
        <div className="mx-auto mt-4 h-px w-24 bg-[hsl(var(--gold))]/60" />
        <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground">
          {tr.subtitle}
        </p>
      </div>

      {/* Plans */}
      <div className="mx-auto grid max-w-4xl gap-6 md:grid-cols-2">
        {PLANS.map((plan) => (
          <div
            key={plan.id}
            data-testid={`pricing-card-${plan.id}`}
            className={`relative overflow-hidden rounded-lg border bg-card p-8 shadow-2xl transition-all ${
              plan.accent
                ? "border-[hsl(var(--gold))] shadow-[hsl(var(--gold))]/10"
                : "border-border"
            }`}
          >
            {plan.accent && (
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[hsl(var(--gold))]/30 via-[hsl(var(--gold))] to-[hsl(var(--gold))]/30" />
            )}
            {plan.badge_key && (
              <div className="mb-3 inline-flex items-center gap-1 rounded-full bg-[hsl(var(--gold))]/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-[hsl(var(--gold))]">
                <Sparkles className="h-3 w-3" />
                {tr[plan.badge_key]}
              </div>
            )}
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
              {tr[plan.label_key]}
            </div>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="font-display text-5xl font-bold text-[hsl(var(--gold))]">
                ${plan.price}
              </span>
              <span className="font-mono text-sm text-muted-foreground">
                {tr[plan.period_key]}
              </span>
            </div>
            <div className="mt-6 h-px w-12 bg-[hsl(var(--gold))]/50" />
            <div className="mt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--gold))]">
              {tr.features_title}
            </div>
            <ul className="mt-4 space-y-2.5">
              {tr.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm leading-relaxed">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--sage))]" strokeWidth={2.4} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              {!user ? (
                <Link to={`/register?type=comercial`} data-testid={`pricing-signup-${plan.id}`}>
                  <Button className="h-11 w-full rounded-md bg-[hsl(var(--gold))] font-semibold text-[hsl(var(--primary-foreground))] shadow-lg shadow-[hsl(var(--gold))]/20 hover:bg-[hsl(var(--gold-soft))]">
                    {lang === "pt" ? "Criar conta profissional" : lang === "es" ? "Crear cuenta profesional" : "Create professional account"}
                  </Button>
                </Link>
              ) : isParticular ? (
                <Button
                  disabled
                  className="h-11 w-full rounded-md"
                  variant="outline"
                >
                  {tr.cta_login}
                </Button>
              ) : isComercialActive ? (
                <Button
                  disabled
                  data-testid={`pricing-active-${plan.id}`}
                  className="h-11 w-full rounded-md bg-[hsl(var(--sage))] text-[hsl(var(--sage-foreground))]"
                >
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  {tr.cta_active}
                </Button>
              ) : (
                <a href={contactHref} data-testid={`pricing-contact-${plan.id}`}>
                  <Button className="h-11 w-full rounded-md bg-[hsl(var(--gold))] font-semibold text-[hsl(var(--primary-foreground))] shadow-lg shadow-[hsl(var(--gold))]/20 hover:bg-[hsl(var(--gold-soft))]">
                    {tr.cta_start}
                  </Button>
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Payment note */}
      <div className="mx-auto mt-10 max-w-3xl rounded-md border border-[hsl(var(--gold))]/30 bg-[hsl(var(--gold))]/[0.06] p-5">
        <div className="flex items-start gap-3">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--gold))]" />
          <div>
            <p className="text-sm leading-relaxed text-foreground/85">{tr.payment_note}</p>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <p className="mx-auto mt-6 max-w-3xl text-center text-xs leading-relaxed text-muted-foreground">
        {tr.disclaimer}
      </p>
    </div>
  );
}
